import React, { useMemo } from 'react';
import { groupTasksByCampaign } from '../utils/groupTasksByCampaign';
import { MissionsSection } from './MissionsSection';
import CampaignDivider from './CampaignDivider';
import AvailableMissionItem from './AvailableMissionItem';
import WaitingMissionItem from './WaitingMissionItem';

const renderGrouped = (groups, renderFn) =>
  groups.map(({ campaign, tasks: groupTasks }) => (
    <React.Fragment key={campaign.id}>
      <CampaignDivider campaign={campaign} />
      {groupTasks.map(renderFn)}
    </React.Fragment>
  ));

/**
 * Ready-to-start missions, plus timer-gated ones that are otherwise unblocked.
 * In multi-campaign mode, missions are grouped by campaign with dividers.
 */
export const AvailableMissionsList = ({
  tasks,
  waitingTasks = [],
  activeTasks,
  isCollapsed,
  onToggle,
  onGoToTask,
  onMissionHover,
  onMissionLeave,
  isMultiCampaignMode = false,
}) => {
  const groupedAvailable = useMemo(() => {
    if (!isMultiCampaignMode || tasks.length === 0) return null;
    return groupTasksByCampaign(tasks);
  }, [tasks, isMultiCampaignMode]);

  const groupedWaiting = useMemo(() => {
    if (!isMultiCampaignMode || waitingTasks.length === 0) return null;
    return groupTasksByCampaign(waitingTasks);
  }, [waitingTasks, isMultiCampaignMode]);

  const hoverProps = (task) => ({
    onGoToTask,
    onMouseEnter: () => onMissionHover?.(task.id),
    onMouseLeave: () => onMissionLeave?.(),
  });

  const renderAvailable = (task) => (
    <AvailableMissionItem key={task.id} task={task} {...hoverProps(task)} />
  );

  const renderWaiting = (task) => (
    <WaitingMissionItem key={task.id} task={task} {...hoverProps(task)} />
  );

  const hasAvailable = tasks.length > 0;
  const hasWaiting = waitingTasks.length > 0;

  return (
    <MissionsSection
      title="AVAILABLE MISSIONS"
      count={tasks.length + waitingTasks.length}
      isCollapsed={isCollapsed}
      onToggle={onToggle}
    >
      {!hasAvailable && !hasWaiting ? (
        <div className="flowchart-hud__missions-empty">
          {activeTasks.length > 0 ? 'Focus on active missions!' : '✨ All caught up!'}
        </div>
      ) : (
        <>
          {hasAvailable && (
            isMultiCampaignMode && groupedAvailable
              ? renderGrouped(groupedAvailable, renderAvailable)
              : tasks.map(renderAvailable)
          )}
          {hasWaiting && (
            <>
              <div className="flowchart-hud__missions-subhead">Starting soon</div>
              {isMultiCampaignMode && groupedWaiting
                ? renderGrouped(groupedWaiting, renderWaiting)
                : waitingTasks.map(renderWaiting)}
            </>
          )}
        </>
      )}
    </MissionsSection>
  );
};
